import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Popover,
  Typography
} from "@mui/material";
import { ProgressButton } from "@qubit/autoparts";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";

import useFlag from "~/config/flags";
import { usePopoverTrigger } from "~/features/andon/andonBoard/hooks/usePopoverTrigger";
import { useGetAllFulfillmentCentersQuery } from "~/redux/warehouse/fulfillmentCenter.hooks";

import FcGridSelection from "./FcGridSelection";

type AddGridControlProps = {
  onAddWorkstations: (workstationIds: string[], fcId?: string) => void;
  siteFcId?: string;
  workingWorkstationIds?: string[];
};

export default function AddGridControl({
  onAddWorkstations,
  siteFcId,
  workingWorkstationIds = []
}: AddGridControlProps) {
  const { t } = useTranslation();
  const { enableAndonOtherFulfillmentCenters } = useFlag();
  const { anchorEl, open, triggerProps, close } = usePopoverTrigger();
  const { data: allFcs = [] } = useGetAllFulfillmentCentersQuery();

  const sortedFcs = useMemo(() => {
    if (!siteFcId) return allFcs;
    const site = allFcs.filter((fc) => fc.fulfillmentCenterId === siteFcId);
    const others = allFcs.filter((fc) => fc.fulfillmentCenterId !== siteFcId);
    return [...site, ...others];
  }, [allFcs, siteFcId]);

  const siteFirst =
    !!siteFcId && sortedFcs[0]?.fulfillmentCenterId === siteFcId;

  const handleAddWorkstations = (workstationIds: string[], fcId?: string) => {
    onAddWorkstations(workstationIds, fcId);
    close();
  };

  return (
    <>
      <ProgressButton
        emphasis="high"
        size="small"
        variant="contained"
        color="secondary"
        startIcon={<AddIcon />}
        {...triggerProps}
      >
        {t("add grid")}
      </ProgressButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: 320, maxHeight: 480, overflowY: "auto" }}>
          {sortedFcs.length > 1 ? (
            sortedFcs.map((fc, index) => {
              const isSite = !!siteFcId && fc.fulfillmentCenterId === siteFcId;
              if (!enableAndonOtherFulfillmentCenters && siteFcId && !isSite) {
                return null;
              }
              const showOthersDivider =
                enableAndonOtherFulfillmentCenters && siteFirst && index === 1;
              return (
                <Fragment key={fc.fulfillmentCenterId}>
                  {showOthersDivider && (
                    <Divider sx={{ my: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t("other fulfillment centers")}
                      </Typography>
                    </Divider>
                  )}
                  <Accordion
                    defaultExpanded={index === 0}
                    disableGutters
                    TransitionProps={{ unmountOnExit: true }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">
                        {fc.abbreviation || fc.name}
                        {isSite ? ` (${t("current")})` : ""}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <FcGridSelection
                        fcId={fc.fulfillmentCenterId}
                        workingWorkstationIds={workingWorkstationIds}
                        onAddWorkstations={handleAddWorkstations}
                      />
                    </AccordionDetails>
                  </Accordion>
                </Fragment>
              );
            })
          ) : (
            <FcGridSelection
              fcId={sortedFcs[0]?.fulfillmentCenterId}
              workingWorkstationIds={workingWorkstationIds}
              onAddWorkstations={handleAddWorkstations}
            />
          )}
        </Box>
      </Popover>
    </>
  );
}
